import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCan } from '@/hooks/useCan';
import { useT } from '@/contexts/I18nContext';
import { getDailyReports, type DailyReportDto } from '@/api/dailyReports';

import { ReportBody, type ReportChartTarget } from './day/ReportBody';
import { LayoutKadry, QueueKadry, TablesCard } from './day/Kadry';
import { DayQueueCard, type QueueFocusRequest } from './day/DayQueueCard';
import { StaffArrivalCard } from './day/StaffArrivalCard';
import { DayRacksCard, type RacksFocusRequest } from './day/DayRacksCard';
import { DayTasksCard } from './day/DayTasksCard';
import { DayVisitsCard } from './day/DayVisitsCard';
import { TableStateStrips } from './TableStateStrips';

function StatusBadge({ status }: { status: DailyReportDto['status'] }) {
  const { t } = useT();
  switch (status) {
    case 'confirmed':
      return <Badge variant="success">{t('summaries.statusConfirmed')}</Badge>;
    case 'cancelled':
      return <Badge variant="muted">{t('summaries.statusCancelled')}</Badge>;
    default:
      return <Badge variant="secondary">{t('summaries.statusPending')}</Badge>;
  }
}

// Compact, slightly "cooler" card title used across the report page (the
// default base looks like raw markdown and eats vertical space).
const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

// ── daily report view (the "День" zoom of the merged Отчёт) ─────────────────────
//
// The date is owned by the page header (OverviewReportPage) and passed in, so the
// switcher + date picker share one compact header row instead of stacking. The
// text report is one card, the характерные кадры a separate card, and the queue +
// racks each get their own day-chart card with time-aligned proof photos.
//
// Read-only for everyone. Report validation (edit/confirm/cancel/send + the
// pending/unsent queue navigation) now lives on the Служебные "Проверка отчётов"
// page. The staff-visible remnants here are the status badge, the existing
// behaviour of hiding a cancelled report's body/photos from non-validators,
// and — #264 — the daily_report.manage-gated pencil on the tables card that
// lets a validator drop a false dirty-table incident straight from this view
// (no round trip through the separate validation page for that one action).

export function DailyReportView({ zoneId, date, pending = false }: { zoneId: string; date: string; pending?: boolean }) {
  const { t } = useT();

  // Validators (daily_report.manage) additionally see the status badge and the
  // full body of cancelled reports; everyone else gets the clean read view.
  const canManage = useCan('daily_report.manage');

  // «Посмотреть» buttons in the report body open the relevant day-chart card's
  // fullscreen lightbox zoomed to the problem window. Route the click to the
  // right card via a nonce-keyed focus request (so a repeat click re-fires).
  const [racksFocus, setRacksFocus] = useState<RacksFocusRequest | null>(null);
  const [queueFocus, setQueueFocus] = useState<QueueFocusRequest | null>(null);
  const nonceRef = useRef(0);
  const openChart = useCallback(
    (
      target: ReportChartTarget,
      domain: [number, number],
      rackName?: string,
      window?: [number, number],
      rackId?: number,
    ) => {
      nonceRef.current += 1;
      if (target === 'racks') setRacksFocus({ domain, rackName, rackId, window, nonce: nonceRef.current });
      else setQueueFocus({ domain, nonce: nonceRef.current });
    },
    [],
  );

  // item 9: a problem-zone link (from a report, Telegram, wherever) used to
  // just land here on the whole-day report — the reader still had to find
  // and open the right rack/queue chart themselves. `build_problem_zone_link`
  // / `get_checkout_problem_zone_link` (BE) now carry target=racks|queue (+
  // rack=<name>) alongside the existing from/to window; auto-fire the SAME
  // openChart a "Посмотреть" button click does, once, on arrival — same
  // fullscreen deep-link the in-page buttons already give.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoFocusedRef = useRef(false);
  useEffect(() => {
    if (autoFocusedRef.current) return;
    const target = searchParams.get('target');
    const fromMs = Number(searchParams.get('from'));
    const toMs = Number(searchParams.get('to'));
    if (
      (target === 'racks' || target === 'queue') &&
      Number.isFinite(fromMs) &&
      Number.isFinite(toMs) &&
      toMs > fromMs
    ) {
      autoFocusedRef.current = true;
      // The URL carries the raw window; pad it the same way periodFromHref
      // does for in-page clicks so both paths open the same zoom.
      const pad = (toMs - fromMs) * 0.1;
      const rackIdRaw = Number(searchParams.get('rack_id'));
      openChart(
        target,
        [fromMs - pad, toMs + pad],
        searchParams.get('rack') ?? undefined,
        [fromMs, toMs],
        Number.isInteger(rackIdRaw) && rackIdRaw > 0 ? rackIdRaw : undefined,
      );
      // Consume the one-shot params so paging/refresh doesn't keep re-firing
      // it — the date/zone params (z, zone, date) stay untouched.
      const next = new URLSearchParams(searchParams);
      next.delete('target');
      next.delete('from');
      next.delete('to');
      next.delete('rack');
      next.delete('rack_id');
      setSearchParams(next, { replace: true });
    }
    // Only evaluate once per mount (searchParams itself changes as a
    // consequence of the cleanup above) — the ref guard is the real gate.
  }, [searchParams, openChart]);

  const { data, isLoading, error, mutate } = useSWR(
    zoneId ? ['daily-reports', zoneId, date] : null,
    // Ответ помечен своим днём: с keepPreviousData на экране оставался отчёт прошлого дня, пока новый
    // грузится, а если запрос нового падал — оставался насовсем («не перелистывается», #3271).
    () => getDailyReports(zoneId, date, date).then((page) => ({ ...page, forDate: date })),
    // Day stepping keeps the previous day's report on screen while the next
    // loads — the page doesn't collapse to a spinner, so scroll survives.
    // No focus-revalidation — a refetch on refocus reflows the report cards.
    { keepPreviousData: true, revalidateOnFocus: false },
  );

  const stale = pending || (!!data && data.forDate !== date);
  const report: DailyReportDto | undefined = data && !(stale && error) ? data.results[0] : undefined;

  const isCancelled = report?.status === 'cancelled';

  const layoutPhotos = report?.photo_highlights?.layout ?? [];
  const queuePhotos = report?.photo_highlights?.queue ?? [];
  const tablePhotos = report?.photo_highlights?.tables ?? [];
  // Old stored reports lack the flag → show the tables card only with photos.
  const tablesConfigured = report?.photo_highlights?.tables_configured ?? false;

  // Balance the кадры columns: with a tall Выкладка contact sheet on the left
  // (3+ racks), lay the queue frames 2×2 so the right column keeps up; with a
  // short one, a single row of four wastes less height.
  const layoutRackCount = new Set(layoutPhotos.map((p) => p.rack_name ?? '—')).size;
  const queueColumns: 2 | 4 = layoutRackCount >= 3 ? 2 : 4;

  return (
    <div className="space-y-4">
      {stale && !error && (
        <div className="pointer-events-none fixed inset-x-0 top-20 z-30 flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border bg-background/95 px-3 py-1 text-xs text-muted-foreground shadow-sm">
            <Loader2 className="size-3.5 animate-spin" />{t('summaries.loading')}
          </span>
        </div>
      )}

      {!report && isLoading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 size-5 animate-spin" />
          <span>{t('summaries.loading')}</span>
        </div>
      )}

      {!isLoading && error && (!data || stale) && (
        <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />
      )}

      {!isLoading && !stale && !error && !report && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t('summaries.noReport')}
          </CardContent>
        </Card>
      )}

      {report && (
        <>
          {/* Text report on the left, the day charts (Очередь / Выкладка) directly
              to its right — mirroring the report-validation layout. The
              характерные кадры move below the fold. */}
          <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2">
            {/* ── Card 1: the chat report text ── */}
            <Card className="flex flex-col">
              <CardHeader className="flex flex-row items-center justify-between gap-3 pt-4 pb-2">
                <CardTitle className={CARD_TITLE}>{t('summaries.chatReportTitle')}</CardTitle>
                {canManage && <StatusBadge status={report.status} />}
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-3">
                {isCancelled && !canManage ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t('summaries.cancelledPlaceholder')}
                  </p>
                ) : (
                  <ReportBody html={report.body_html} onOpenChart={openChart} />
                )}
              </CardContent>
            </Card>

            {/* ── Right column: the day charts (Выкладка on top, Очередь below) ── */}
            {!(isCancelled && !canManage) && (
              <div className="flex flex-col gap-4">
                <DayRacksCard
                  zoneId={zoneId}
                  day={date}
                  focusRequest={racksFocus}
                  onFocusHandled={() => setRacksFocus(null)}
                />
                <DayQueueCard
                  zoneId={zoneId}
                  day={date}
                  focusRequest={queueFocus}
                  onFocusHandled={() => setQueueFocus(null)}
                />
                {/* Приход к открытию (#1324) — только у филиалов с включённой функцией. */}
                <StaffArrivalCard zoneId={zoneId} from={date} to={date} compact />
              </div>
            )}
          </div>

          {/* ── Характерные кадры дня — side by side: Выкладка on the LEFT
              (its per-rack contact sheet is wide), Очередь + «Самый долгий
              неубранный стол» sharing the RIGHT column. Full-width Выкладка made
              the frames uncomfortably large; halving it keeps them legible. On
              phones everything stacks. ── */}
          {!(isCancelled && !canManage) &&
            (layoutPhotos.length > 0 ||
              queuePhotos.length > 0 ||
              tablePhotos.length > 0 ||
              tablesConfigured) && (
              /* items-stretch + h-full/flex-1: the two columns read as ONE
                 row of equal height (like the Отчёт для чата row), even when
                 their contents differ. */
              <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2">
                {/* Left: Выкладка */}
                {layoutPhotos.length > 0 && (
                  <Card className="h-full">
                    <CardHeader className="pt-4 pb-2">
                      <CardTitle className={CARD_TITLE}>{t('summaries.kadryLayoutTitle')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <LayoutKadry photos={layoutPhotos} />
                    </CardContent>
                  </Card>
                )}

                {/* Right: Очередь + неубранные столы */}
                {(queuePhotos.length > 0 || tablePhotos.length > 0 || tablesConfigured) && (
                  <div className="flex h-full flex-col gap-4">
                    {queuePhotos.length > 0 && (
                      <Card>
                        <CardHeader className="pt-4 pb-2">
                          <CardTitle className={CARD_TITLE}>{t('summaries.kadryQueueTitle')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <QueueKadry photos={queuePhotos} columns={queueColumns} />
                        </CardContent>
                      </Card>
                    )}

                    <TablesCard
                      photos={tablePhotos}
                      configured={tablesConfigured}
                      className="flex-1"
                      reportId={report.id}
                      canManage={canManage}
                      onChanged={() => mutate()}
                    />
                  </div>
                )}
              </div>
            )}

          {/* ── Tasks (checklist run) merged card — hidden when no tasks that day ── */}
          {!(isCancelled && !canManage) && <DayTasksCard zoneId={zoneId} day={date} />}

          {/* ── Visits (VISITS-3, #176) — hidden when the feature is off or no visits that day ── */}
          {!(isCancelled && !canManage) && <DayVisitsCard zoneId={zoneId} day={date} />}

          {/* ── Table state strips — the newest section sits at the very bottom
              of the day's итоги, after the photos and the checklist stats. ── */}
          {!(isCancelled && !canManage) && (
            <TableStateStrips
              zoneId={zoneId}
              from={date}
              to={date}
              title={t('report.tables.stripsDayTitle')}
              experimental
              deepLink
            />
          )}
        </>
      )}
    </div>
  );
}
